import * as React from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import { IconButton } from '@mui/material';
import {
    Button,
    DataProvider,
    useDataProvider,
    useNotify,
    useRecordContext,
    useTranslate,
} from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';
import { StatsTooltip } from '../../components/statistics/common';

interface CampaignDataProvider extends DataProvider {
    downloadCampaignCsv: (guid: string) => Promise<void>;
    downloadCampaignResponsesCsv: (guid: string) => Promise<void>;
    emailCampaignExport: (guid: string) => Promise<void>;
}

const CampaignExportButton = ({
    email = false,
    allResponses = false,
}: {
    email?: boolean;
    allResponses?: boolean;
}) => {
    const record = useRecordContext<{ guid: string; csv_recipient?: string }>();
    const dataProvider = useDataProvider<CampaignDataProvider>();
    const queryClient = useQueryClient();
    const notify = useNotify();
    const translate = useTranslate();
    const [exporting, setExporting] = React.useState(false);

    const downloadCsv = async (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (!record?.guid || exporting) return;
        setExporting(true);
        try {
            if (email) {
                await dataProvider.emailCampaignExport(record.guid);
                await queryClient.invalidateQueries({
                    queryKey: ['campaign-export-deliveries', record.guid],
                });
                notify('resources.campaign.email_success', { type: 'success' });
            } else {
                if (allResponses) {
                    await dataProvider.downloadCampaignResponsesCsv(record.guid);
                } else {
                    await dataProvider.downloadCampaignCsv(record.guid);
                }
            }
        } catch (error) {
            console.error(error);
            notify(email
                ? 'resources.campaign.email_error'
                : 'resources.campaign.export_error', { type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    if (!email) return (
        <Button
            label={allResponses
                ? (exporting
                    ? 'resources.campaign.actions.exporting_responses'
                    : 'resources.campaign.actions.export_responses')
                : exporting
                ? 'resources.campaign.actions.exporting'
                : 'resources.campaign.actions.export'}
            onClick={downloadCsv}
            disabled={!record?.guid || exporting}
        >
            <DownloadIcon />
        </Button>
    );
    const emailLabel = translate(exporting
        ? 'resources.campaign.actions.emailing'
        : 'resources.campaign.actions.email');
    const emailTitle = `${emailLabel} — ${record?.csv_recipient || translate('resources.campaign.recipient_missing')}`;
    return (
        <StatsTooltip
            title={emailTitle}
            followCursor
            placement="top-start"
            componentsProps={{
                tooltip: {
                    sx: {
                        padding: '12px 16px',
                        fontSize: '14px',
                        lineHeight: 1.4,
                    },
                },
            }}
        >
            <span>
                <IconButton
                    aria-label={emailTitle}
                    color="primary"
                    onClick={downloadCsv}
                    disabled={!record?.guid || exporting}
                >
                    <EmailIcon />
                </IconButton>
            </span>
        </StatsTooltip>
    );
};

export default CampaignExportButton;
